import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Badge, Card } from '@safetyhub/ui';
import type { LegalDocumentSiteManagement } from '@safetyhub/shared';
import { useAuthStore } from '../../stores/auth';
import { apiErrorMessage } from '../../lib/api';
import { getLegalDocumentSites, legalDocumentsQueryKeys } from '../../lib/legalDocuments';
import {
  describeAuditedCompanyAccount,
  describeSiteManagement,
} from '../../lib/legalDocumentLabels';
import { ChevronRightIcon } from '../../components/icons';
import { usePermissions } from '../../hooks/usePermissions';
import ImminentPanel from '../../components/legal-documents/ImminentPanel';
import AgencyOverlayEntryLink from '../../components/legal-documents/AgencyOverlayEntryLink';
import CompliancePanel from '../../components/legal-documents/CompliancePanel';
import PastSitesPanel from '../../components/legal-documents/PastSitesPanel';

const MANAGEMENT_TONE: Record<LegalDocumentSiteManagement, 'brand' | 'violet' | 'slate'> = {
  SELF: 'brand',
  AGENCY: 'violet',
  UNREGISTERED: 'slate',
};

/**
 * 법정 서류 관리 — 첫 화면은 **현장 선택**이다(결정 `2026-10-23-legal-document-obligation-management.md`
 * ⓔ *고객 화면 입구* #2). 업체가 공장 여럿을 가지고 공장마다 직접 관리·서로 다른 기관 대행이 섞일 수
 * 있어 현장 단위로 연다.
 *
 * `UNREGISTERED`(수검업체 링크 없음)도 목록에 **남긴다** — 그 현장은 의무가 실체화되지 않으므로
 * 「왜 비었나」를 이 자리에서 말해야 한다(빼면 사용자는 현장이 사라진 것으로 읽는다).
 */
export default function LegalDocumentsSitesPage() {
  const companyId = useAuthStore((s) => s.currentMembership?.companyId);
  const { can } = usePermissions();
  const canView = can('obligation.view');
  const { data, isLoading, error } = useQuery({
    queryKey: legalDocumentsQueryKeys.sites(companyId ?? ''),
    queryFn: () => getLegalDocumentSites(companyId!),
    enabled: !!companyId,
  });

  const sites = data?.sites ?? [];

  return (
    <div className="mx-auto max-w-4xl px-4 py-6 sm:px-0">
      <div className="mb-6 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-[26px] font-extrabold tracking-tight text-ink-900">법정 서류 관리</h1>
          <p className="mt-1 text-[15px] font-medium text-ink-500">
            현장을 고르면 그 현장에 적용되는 법정 의무와 이번 기간의 이행 상태를 봅니다.
          </p>
        </div>
        {/* 대행 기관의 서식 변형 입구 — 서버가 「대행 현장이 있는 조직」일 때만 그린다(그 컴포넌트 doc) */}
        {companyId && canView && <AgencyOverlayEntryLink companyId={companyId} />}
      </div>

      {/* L20 — 접근 현장 «전체»의 이번 기간 이행률. 보는 키(view)로 열고, 실무 버튼은 현장 화면이 manage·exempt로 숨긴다 */}
      {companyId && canView && <CompliancePanel companyId={companyId} />}

      {/* L1 — 접근 현장 «전체»의 기한이 다가오는 문서. 그 API 가 obligation.view 를 요구하므로 같은 키로 가린다 */}
      {companyId && canView && <ImminentPanel companyId={companyId} />}

      {isLoading ? (
        <div className="flex justify-center py-12" role="status" aria-label="불러오는 중">
          <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-brand-600" />
        </div>
      ) : error ? (
        <div role="alert" className="rounded-card border border-rose-200 bg-rose-50 p-4">
          <p className="text-sm font-medium text-rose-700">
            {apiErrorMessage(error) ?? '현장 목록을 불러오지 못했습니다.'}
          </p>
        </div>
      ) : sites.length === 0 ? (
        <Card padding="none" radius="lg" elevation="md" className="py-12 text-center">
          <p className="text-sm font-medium text-ink-900">법정 서류를 관리할 현장이 없습니다</p>
          <p className="mt-1 text-sm text-ink-500">현장의 수검업체가 등록되면 여기에 표시됩니다.</p>
        </Card>
      ) : (
        <Card padding="none" radius="lg" elevation="md" className="overflow-hidden">
          <ul className="divide-y divide-ink-200" aria-label="현장 목록">
            {sites.map((site) => {
              const account = describeAuditedCompanyAccount(site.auditedCompanyHasAccount);
              return (
                <li key={site.siteId}>
                  <Link
                    to={`/legal-documents/sites/${encodeURIComponent(site.siteId)}`}
                    className="flex items-center gap-3 px-4 py-4 hover:bg-surface-hover sm:px-6"
                  >
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-bold text-ink-900">{site.siteName}</p>
                      <p className="mt-0.5 flex min-w-0 items-center gap-1.5 text-xs text-ink-500">
                        <span className="truncate">
                          {site.auditedCompanyName
                            ? `수검업체 ${site.auditedCompanyName}`
                            : '수검업체가 아직 등록되지 않았습니다'}
                        </span>
                        {account && (
                          <span
                            className={`flex-none rounded px-1 font-medium ${
                              site.auditedCompanyHasAccount
                                ? 'bg-emerald-50 text-emerald-700'
                                : 'bg-ink-100 text-ink-600'
                            }`}
                            title={account.hint}
                          >
                            {account.label}
                          </span>
                        )}
                      </p>
                    </div>
                    <Badge tone={MANAGEMENT_TONE[site.management]} size="sm">
                      {describeSiteManagement(site)}
                    </Badge>
                    <ChevronRightIcon className="h-4 w-4 flex-none text-ink-400" />
                  </Link>
                </li>
              );
            })}
          </ul>
        </Card>
      )}

      {/* L32 — 대행 계약이 끝난 현장의 지난 기록(읽기 전용). 없으면 그리지 않는다(그 컴포넌트 doc) */}
      {companyId && <PastSitesPanel companyId={companyId} />}
    </div>
  );
}
